/**
 * Peluquería DLC — selector de sede, rejillas de equipo y precios, navegación.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Un peluquero puede estar en varias sedes: data-loc-slug admite una lista
 * separada por espacios.
 * @param {HTMLElement} el
 * @param {string} slug
 */
function matchesLocation(el, slug) {
  if (!slug) return true;
  const slugs = (el.dataset.locSlug || '').split(/\s+/).filter(Boolean);
  return slugs.length === 0 || slugs.includes(slug);
}

/**
 * @param {HTMLElement} hero
 * @param {boolean} reduceMotion
 */
function bindHeroParallax(hero, reduceMotion) {
  const media = hero.querySelector('[data-dlc-pel-media]');
  if (!(media instanceof HTMLElement) || reduceMotion) return () => {};

  const mobileMq = window.matchMedia('(max-width: 749px)');
  /** @type {number} */
  let frame = 0;

  const update = () => {
    frame = 0;
    if (mobileMq.matches) {
      hero.style.setProperty('--dlc-pel-parallax', '0');
      return;
    }
    const rect = hero.getBoundingClientRect();
    const viewH = window.innerHeight || 1;
    const progress = Math.min(1, Math.max(0, -rect.top / (rect.height + viewH)));
    hero.style.setProperty('--dlc-pel-parallax', String(progress * 48));
  };

  const onScroll = () => {
    if (frame) return;
    frame = requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', onScroll, { passive: true });
  mobileMq.addEventListener('change', update);
  return () => {
    window.removeEventListener('scroll', onScroll);
    mobileMq.removeEventListener('change', update);
    if (frame) cancelAnimationFrame(frame);
  };
}

/**
 * @param {boolean} reduceMotion
 */
function bindFooterReveal(reduceMotion) {
  const footer = document.querySelector('body.dlc-peluqueria-page .dlc-site-footer');
  if (!(footer instanceof HTMLElement)) return () => {};

  if (reduceMotion || !('IntersectionObserver' in window)) {
    footer.classList.add('is-inview');
    return () => {};
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          footer.classList.add('is-inview');
          observer.disconnect();
          break;
        }
      }
    },
    { threshold: 0.12 }
  );

  observer.observe(footer);
  return () => observer.disconnect();
}

/**
 * Selector de sede: cambia la sede activa y avisa a las rejillas de equipo y precios.
 */
function bindLocationSelector() {
  const root = document.querySelector('[data-dlc-pel-locations]');
  if (!(root instanceof HTMLElement)) return () => {};

  const track = root.querySelector('[data-dlc-pel-loc-track]');
  const buttons = /** @type {HTMLButtonElement[]} */ (
    Array.from(root.querySelectorAll('[data-dlc-pel-loc]'))
  );
  if (!(track instanceof HTMLElement) || buttons.length === 0) return () => {};

  /**
   * @param {number} index
   * @param {HTMLButtonElement} button
   */
  const select = (index, button) => {
    track.style.setProperty('--dlc-pel-loc-index', String(index));
    document.body.dataset.dlcPelLocation = button.dataset.locSlug || '';

    buttons.forEach((btn, i) => {
      const active = i === index;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
      btn.tabIndex = active ? 0 : -1;
    });

    document.dispatchEvent(
      new CustomEvent('dlc:peluqueria-location', {
        detail: {
          index,
          name: button.dataset.locName || '',
          slug: button.dataset.locSlug || '',
          bookingUrl: button.dataset.bookingUrl || '',
        },
      })
    );
  };

  /** @param {MouseEvent} event */
  const onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-dlc-pel-loc]');
    if (!(button instanceof HTMLButtonElement)) return;
    select(Number(button.dataset.locIndex || 0), button);
  };

  /** @param {KeyboardEvent} event */
  const onKeydown = (event) => {
    const current = buttons.findIndex((btn) => btn.classList.contains('is-active'));
    let next = current;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = (current + 1) % buttons.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = (current - 1 + buttons.length) % buttons.length;
    } else {
      return;
    }
    event.preventDefault();
    select(next, buttons[next]);
    buttons[next].focus();
  };

  /** @type {number | null} */
  let startX = null;
  /** @param {TouchEvent} event */
  const onTouchStart = (event) => {
    startX = event.changedTouches[0]?.clientX ?? null;
  };
  /** @param {TouchEvent} event */
  const onTouchEnd = (event) => {
    if (startX == null) return;
    const endX = event.changedTouches[0]?.clientX ?? startX;
    const delta = endX - startX;
    startX = null;
    if (Math.abs(delta) < 40) return;
    const current = buttons.findIndex((btn) => btn.classList.contains('is-active'));
    const next =
      delta < 0
        ? Math.min(buttons.length - 1, current + 1)
        : Math.max(0, current - 1);
    if (next !== current) select(next, buttons[next]);
  };

  track.addEventListener('click', onClick);
  track.addEventListener('keydown', onKeydown);
  track.addEventListener('touchstart', onTouchStart, { passive: true });
  track.addEventListener('touchend', onTouchEnd, { passive: true });

  select(0, buttons[0]);

  return () => {
    track.removeEventListener('click', onClick);
    track.removeEventListener('keydown', onKeydown);
    track.removeEventListener('touchstart', onTouchStart);
    track.removeEventListener('touchend', onTouchEnd);
  };
}

function forceDrawerMenu() {
  const header = document.querySelector('#header-component');
  if (!(header instanceof HTMLElement)) return () => {};

  const apply = () => {
    header.dataset.menuStyle = 'drawer';
  };

  apply();
  const id = window.setInterval(apply, 400);
  window.setTimeout(() => window.clearInterval(id), 2500);
  window.addEventListener('resize', apply, { passive: true });

  return () => {
    window.clearInterval(id);
    window.removeEventListener('resize', apply);
  };
}

/**
 * Al abrir la navegación, la página visible se encoge hacia la esquina
 * inferior derecha (estilo CRISP). Recorta el wrapper al área que había en
 * pantalla y escala desde el borde inferior derecho de esa área.
 */
function bindNavShrink() {
  const wrap = document.querySelector('[data-dlc-pel-shrink]');
  if (!(wrap instanceof HTMLElement)) return () => {};

  /** @type {number | null} */
  let clearTimer = null;

  const measure = () => {
    const rect = wrap.getBoundingClientRect();
    const vh = window.innerHeight;
    const visibleTopLocal = Math.max(0, -rect.top);
    const visibleBottomLocal = Math.min(rect.height, vh - rect.top);
    const clipTop = visibleTopLocal;
    const clipBottom = Math.max(0, rect.height - visibleBottomLocal);
    const originY = vh - rect.top;

    wrap.style.setProperty('--dlc-pel-shrink-clip-top', `${Math.round(clipTop)}px`);
    wrap.style.setProperty('--dlc-pel-shrink-clip-bottom', `${Math.round(clipBottom)}px`);
    wrap.style.setProperty('--dlc-pel-shrink-oy', `${Math.round(originY)}px`);
  };

  const open = () => {
    if (clearTimer) {
      window.clearTimeout(clearTimer);
      clearTimer = null;
    }
    measure();
    wrap.setAttribute('data-dlc-pel-clip', '');
    // doble frame: aplica clip/origen antes de iniciar la transición
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.body.setAttribute('data-dlc-pel-shrunk', '');
      });
    });
  };

  const close = () => {
    document.body.removeAttribute('data-dlc-pel-shrunk');
    const done = () => {
      wrap.removeAttribute('data-dlc-pel-clip');
      wrap.style.removeProperty('--dlc-pel-shrink-clip-top');
      wrap.style.removeProperty('--dlc-pel-shrink-clip-bottom');
      wrap.style.removeProperty('--dlc-pel-shrink-oy');
      clearTimer = null;
    };
    clearTimer = window.setTimeout(done, 800);
  };

  const isOpen = () => document.documentElement.hasAttribute('data-dlc-nav-open');
  let wasOpen = isOpen();

  const observer = new MutationObserver(() => {
    const now = isOpen();
    if (now === wasOpen) return;
    wasOpen = now;
    if (now) open();
    else close();
  });
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-dlc-nav-open'] });

  /** Pulsar la página encogida cierra el menú */
  const onClick = (event) => {
    if (!document.body.hasAttribute('data-dlc-pel-shrunk')) return;
    const summary = document.querySelector('header-drawer details.menu-drawer-container.menu-open > summary');
    if (summary instanceof HTMLElement) {
      event.preventDefault();
      summary.click();
    }
  };
  wrap.addEventListener('click', onClick);

  const onResize = () => {
    if (document.body.hasAttribute('data-dlc-pel-shrunk')) measure();
  };
  window.addEventListener('resize', onResize, { passive: true });

  if (wasOpen) open();

  return () => {
    observer.disconnect();
    wrap.removeEventListener('click', onClick);
    window.removeEventListener('resize', onResize);
    if (clearTimer) window.clearTimeout(clearTimer);
  };
}

/**
 * Cabecera transparente mientras la portada cubre su franja; a partir de ahi
 * vuelve al fondo blanco del resto de la pagina.
 */
function bindBannerHeader() {
  const banner = document.querySelector('[data-dlc-pel-banner]');
  if (!(banner instanceof HTMLElement)) return () => {};

  const root = document.documentElement;

  const headerHeight = () => {
    const header =
      document.querySelector('#header-component') ||
      document.querySelector('#header-group');
    return header instanceof HTMLElement ? header.offsetHeight : 0;
  };

  /* Barra del navegador al tono de la portada: sin ella queda una franja
     blanca sobre la imagen en movil. */
  const themeColor = document.querySelector('meta[data-dlc-pel-theme-color]');
  const bannerColor = getComputedStyle(banner).backgroundColor || '#0d0d0d';
  const pageColor = getComputedStyle(document.body).backgroundColor || '#ffffff';

  const setState = (over) => {
    root.toggleAttribute('data-dlc-pel-over-banner', over);
    themeColor?.setAttribute('content', over ? bannerColor : pageColor);
  };

  const update = () => {
    setState(banner.getBoundingClientRect().bottom > headerHeight());
  };

  /* Centinela en el borde inferior de la portada: el IntersectionObserver
     avisa en cuanto cruza la franja de la cabecera, sin depender del scroll. */
  const sentinel = document.createElement('span');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:absolute;left:0;bottom:0;width:100%;height:1px;pointer-events:none;';
  banner.appendChild(sentinel);

  /** @type {IntersectionObserver | null} */
  let observer = null;
  const observe = () => {
    observer?.disconnect();
    if (!('IntersectionObserver' in window)) return;
    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        setState(entry.isIntersecting);
      },
      { rootMargin: `-${Math.round(headerHeight())}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
  };

  const onViewportChange = () => {
    update();
    observe();
  };

  update();
  observe();
  document.addEventListener('scroll', update, { capture: true, passive: true });
  window.addEventListener('resize', onViewportChange, { passive: true });

  return () => {
    document.removeEventListener('scroll', update, { capture: true });
    window.removeEventListener('resize', onViewportChange);
    observer?.disconnect();
    sentinel.remove();
    root.removeAttribute('data-dlc-pel-over-banner');
  };
}

/**
 * Rejilla filtrada por sede (equipo y precios): muestra solo las celdas de
 * la sede activa, con un fundido suave al cambiar. Si quedan un numero impar
 * de celdas, la ultima ocupa la fila completa.
 * @param {string} gridSelector
 * @param {string} itemSelector
 * @param {boolean} reduceMotion
 */
function bindLocationGrid(gridSelector, itemSelector, reduceMotion) {
  const grid = document.querySelector(gridSelector);
  if (!(grid instanceof HTMLElement)) return () => {};

  const items = /** @type {HTMLElement[]} */ (Array.from(grid.querySelectorAll(itemSelector)));
  if (items.length === 0) return () => {};

  /** @type {number | null} */
  let swapTimer = null;

  /** @param {string} slug */
  const apply = (slug) => {
    /** @type {HTMLElement[]} */
    const visible = [];

    items.forEach((item) => {
      const match = matchesLocation(item, slug);
      item.hidden = !match;
      item.classList.remove('is-wide');
      if (match) visible.push(item);
    });

    if (visible.length % 2 === 1) {
      visible[visible.length - 1].classList.add('is-wide');
    }
  };

  /** @param {string} slug */
  const swap = (slug) => {
    if (reduceMotion) {
      apply(slug);
      return;
    }

    if (swapTimer) window.clearTimeout(swapTimer);
    grid.classList.add('is-swapping');

    swapTimer = window.setTimeout(() => {
      apply(slug);
      requestAnimationFrame(() => {
        grid.classList.remove('is-swapping');
      });
      swapTimer = null;
    }, 220);
  };

  /** @param {Event} event */
  const onLocation = (event) => {
    const detail = /** @type {CustomEvent<{ slug?: string }>} */ (event).detail;
    swap(detail?.slug || '');
  };

  document.addEventListener('dlc:peluqueria-location', onLocation);
  apply(document.body.dataset.dlcPelLocation || '');

  return () => {
    document.removeEventListener('dlc:peluqueria-location', onLocation);
    if (swapTimer) window.clearTimeout(swapTimer);
  };
}

/**
 * Galería de trabajos: 6 imágenes, 3 visibles, avanza una cada N segundos en
 * bucle continuo. Clona las 3 primeras al final para que el salto de vuelta
 * al inicio sea invisible. Marca `.is-center` en la imagen central (color).
 * Se pausa fuera de pantalla, con la pestaña oculta y al pasar el ratón.
 * @param {boolean} reduceMotion
 * @returns {() => void}
 */
function bindGallery(reduceMotion) {
  const root = document.querySelector('[data-dlc-pel-gallery]');
  const track = root?.querySelector('[data-dlc-pel-gallery-track]');
  if (!(root instanceof HTMLElement) || !(track instanceof HTMLElement)) return () => {};

  const VISIBLE = 3;
  const originals = Array.from(track.querySelectorAll('[data-dlc-pel-gallery-slide]'));
  const total = originals.length;
  if (total <= VISIBLE) {
    originals[Math.floor(VISIBLE / 2)]?.classList.add('is-center');
    return () => {};
  }

  // Clones para el bucle sin salto
  originals.slice(0, VISIBLE).forEach((slide) => {
    const clone = /** @type {HTMLElement} */ (slide.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-clone', '');
    track.appendChild(clone);
  });
  const slides = Array.from(track.querySelectorAll('[data-dlc-pel-gallery-slide]'));

  let offset = 0;
  let timer = 0;
  let paused = false;
  let inView = true;
  const intervalMs = Math.max(2, Number(root.dataset.interval) || 4) * 1000;

  const markCenter = () => {
    const centerLogical = (offset + Math.floor(VISIBLE / 2)) % total;
    slides.forEach((slide) => {
      const idx = Number(slide.dataset.index);
      slide.classList.toggle('is-center', idx === centerLogical);
    });
  };

  const apply = (instant = false) => {
    if (instant) track.classList.add('is-instant');
    track.style.setProperty('--dlc-pel-gallery-offset', String(offset));
    if (instant) {
      // Forzar reflow para que el siguiente cambio vuelva a animar
      void track.offsetWidth;
      track.classList.remove('is-instant');
    }
    markCenter();
  };

  const step = () => {
    offset += 1;
    apply(false);
  };

  const onTransitionEnd = (event) => {
    if (event.target !== track || event.propertyName !== 'transform') return;
    if (offset >= total) {
      offset -= total;
      apply(true);
    }
  };

  const start = () => {
    stop();
    if (reduceMotion || paused || !inView || document.hidden) return;
    timer = window.setInterval(step, intervalMs);
  };
  const stop = () => {
    if (timer) window.clearInterval(timer);
    timer = 0;
  };

  const onEnter = () => {
    paused = true;
    stop();
  };
  const onLeave = () => {
    paused = false;
    start();
  };
  const onVisibility = () => start();

  const io = new IntersectionObserver(
    (entries) => {
      inView = entries.some((entry) => entry.isIntersecting);
      start();
    },
    { threshold: 0.2 }
  );

  track.addEventListener('transitionend', onTransitionEnd);
  root.addEventListener('pointerenter', onEnter);
  root.addEventListener('pointerleave', onLeave);
  document.addEventListener('visibilitychange', onVisibility);
  io.observe(root);

  apply(true);
  start();

  return () => {
    stop();
    io.disconnect();
    track.removeEventListener('transitionend', onTransitionEnd);
    root.removeEventListener('pointerenter', onEnter);
    root.removeEventListener('pointerleave', onLeave);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

/**
 * El botón de reserva sigue la sede activa (Almería / Balerma).
 */
function bindBookingLinks() {
  const links = /** @type {HTMLAnchorElement[]} */ (
    Array.from(document.querySelectorAll('[data-dlc-pel-book]'))
  );
  if (links.length === 0) return () => {};

  /** @param {string} url */
  const apply = (url) => {
    const href = (url || '').trim();
    if (!href) return;
    links.forEach((link) => {
      link.href = href;
    });
  };

  /** @param {Event} event */
  const onLocation = (event) => {
    const detail = /** @type {CustomEvent<{ bookingUrl?: string; slug?: string }>} */ (event)
      .detail;
    const fromEvent = (detail?.bookingUrl || '').trim();
    if (fromEvent) {
      apply(fromEvent);
      return;
    }
    const slug = detail?.slug || '';
    const button = slug
      ? document.querySelector(`[data-dlc-pel-loc][data-loc-slug="${CSS.escape(slug)}"]`)
      : document.querySelector('[data-dlc-pel-loc].is-active');
    if (button instanceof HTMLElement) apply(button.dataset.bookingUrl || '');
  };

  document.addEventListener('dlc:peluqueria-location', onLocation);

  const active = document.querySelector('[data-dlc-pel-loc].is-active');
  if (active instanceof HTMLElement) apply(active.dataset.bookingUrl || '');

  return () => {
    document.removeEventListener('dlc:peluqueria-location', onLocation);
  };
}

function boot() {
  if (!document.body.classList.contains('dlc-peluqueria-page')) return;
  if (document.body.hasAttribute('data-dlc-pel-booted')) return;
  document.body.setAttribute('data-dlc-pel-booted', '');

  const reduceMotion = REDUCED_MOTION.matches;
  /** @type {Array<() => void>} */
  const disposers = [];

  const hero = document.querySelector('[data-dlc-peluqueria-hero]');
  if (hero instanceof HTMLElement) {
    disposers.push(bindHeroParallax(hero, reduceMotion));
  }

  disposers.push(forceDrawerMenu());
  disposers.push(bindNavShrink());
  disposers.push(bindFooterReveal(reduceMotion));
  disposers.push(bindBannerHeader());
  disposers.push(bindLocationGrid('[data-dlc-pel-team-grid]', '[data-dlc-pel-team-cell]', reduceMotion));
  disposers.push(bindLocationGrid('[data-dlc-pel-prices-grid]', '[data-dlc-pel-price-col]', reduceMotion));
  disposers.push(bindBookingLinks());
  disposers.push(bindLocationSelector());
  disposers.push(bindGallery(reduceMotion));

  requestAnimationFrame(() => {
    document.body.setAttribute('data-dlc-pel-ready', '');
  });

  window.addEventListener(
    'pagehide',
    () => {
      disposers.forEach((dispose) => dispose());
    },
    { once: true }
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
